import React, { useState, useEffect } from 'react';
import {
  X,
  User,
  Calendar,
  ClipboardList,
  UserCheck,
  Clock,
  Save,
  Loader2,
  Info
} from 'lucide-react';
import styles from './LeaveRequestDrawer.module.css';
import api from '../../api/axios';
import toast from 'react-hot-toast';
import CustomDatePicker from '../../components/Common/DatePicker/CustomDatePicker';
import CustomSelect from '../../components/Common/Select/CustomSelect';
import PaginatedSelect from '../../components/Common/Select/PaginatedSelect';

const LeaveRequestDrawer = ({ isOpen, onClose, onSave, request = null }) => {
  const [formData, setFormData] = useState({
    employee_id: '',
    leave_type_id: '',
    leave_duration: 'full_day',
    from_date: '',
    to_date: '',
    reason: ''
  });

  const [loading, setLoading] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);

  const fetchDropdownData = async () => {
    // Dropdowns are fetched via PaginatedSelect
  };

  useEffect(() => {
    if (isOpen) {
      fetchDropdownData();
    }
  }, [isOpen]);

  useEffect(() => {
    if (request && isOpen) {
      setFormData({
        employee_id: request.employee_id?.toString() || '',
        leave_type_id: request.leave_type_id?.toString() || '',
        leave_duration: request.leave_duration || 'full_day',
        from_date: request.from_date || '',
        to_date: request.to_date || '',
        reason: request.reason || ''
      });
    } else if (!request) {
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const isAdmin = storedUser.role?.toLowerCase() === 'admin';
      const defaultEmployeeId = !isAdmin ? (storedUser.employee_id || storedUser.id)?.toString() : '';

      setFormData({
        employee_id: defaultEmployeeId,
        leave_type_id: '',
        leave_duration: 'full_day',
        from_date: '',
        to_date: '',
        reason: ''
      });
    }
  }, [isOpen, request]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData(prev => ({
      ...prev,
      [name]: value || ''
    }));
  };

  const handleDateChange = (name, date) => {
    setFormData(prev => ({
      ...prev,
      [name]: date ? date.toLocaleDateString('en-CA') : ''
    }));
  };

  const handleDurationChange = (duration) => {
    setFormData(prev => ({ ...prev, leave_duration: duration }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const userId = user.id;

    const payload = {
      ...formData,
      ...(request ? { updated_by: userId } : { created_by: userId })
    };

    try {
      if (request) {
        await api.put(`/leave/${request.id}`, payload);
        toast.success('Leave request updated successfully');
      } else {
        await api.post('/leave/apply', payload);
        toast.success('Leave request submitted successfully');
      }
      onSave();
      onClose();
    } catch (error) {
      console.error('Error submitting leave request:', error);
      toast.error(error.response?.data?.message || 'Failed to submit leave request');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles['drawer-backdrop']} onClick={onClose}>
      <div className={styles['drawer-container']} onClick={e => e.stopPropagation()}>
        <div className={styles['drawer-header']}>
          <div className={styles['header-title-wrapper']}>
            <h2>{request ? 'Edit Leave Request' : 'New Leave Request'}</h2>
            <p>Submit your request for time off to your supervisor.</p>
          </div>
          <button className={styles['close-btn']} onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className={styles['drawer-form']}>
          <div className={styles['form-scroll-area']}>
            {isFetching ? (
              <div className={styles['loading-container']}>
                <Loader2 size={40} className={styles['spinner']} />
                <span>Fetching details...</span>
              </div>
            ) : (
              <>
                {/* Employee ID */}
                <div className={styles['form-group']}>
                  <label><User size={16} /> Employee Name <span className={styles['required-star']}>*</span></label>
                  {(() => {
                    const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
                    const isAdmin = storedUser.role?.toLowerCase() === 'admin';
                    
                    if (isAdmin) {
                      return (
                        <PaginatedSelect
                          endpoint="/employees-dropdown"
                          valueKey="id"
                          labelKey="employee_name"
                          value={formData.employee_id}
                          onChange={(val) => handleSelectChange('employee_id', val)}
                          placeholder="Select Employee Name"
                          formatOptionLabel={(option) => `${option.label} (${option.original?.designation_name || 'N/A'})`}
                          required
                        />
                      );
                    } else {
                      return (
                        <div className={`${styles['form-input']} ${styles['readonly-input']}`}>
                          {storedUser.name || 'Current User'}
                        </div>
                      );
                    }
                  })()}
                </div>

                {/* Leave Type Dropdown */}
                <div className={styles['form-group']}>
                  <label><ClipboardList size={16} /> Leave Type <span className={styles['required-star']}>*</span></label>
                  <PaginatedSelect
                    endpoint="/leave-type/dropdown"
                    valueKey="id"
                    labelKey="name"
                    value={formData.leave_type_id}
                    onChange={(val) => handleSelectChange('leave_type_id', val)}
                    placeholder="Select Leave Type"
                    required
                  />
                </div>

                {/* Status (Read-only for new) */}
                {!request && (
                  <div className={styles['form-group']}>
                    <label><Clock size={16} /> Current Status</label>
                    <div className={styles['status-badge']}>
                      <Clock size={14} />
                      <span>Pending</span>
                    </div>
                  </div>
                )}

                {/* Dates Grid */}
                <div className={styles['form-row']}>
                  <div className={styles['form-group']}>
                    <label><Calendar size={16} /> From Date <span className={styles['required-star']}>*</span></label>
                    <CustomDatePicker
                      selected={formData.from_date}
                      onChange={(date) => handleDateChange('from_date', date)}
                      placeholderText="mm/dd/yyyy"
                      required
                    />
                  </div>
                  <div className={styles['form-group']}>
                    <label><Calendar size={16} /> To Date <span className={styles['required-star']}>*</span></label>
                    <CustomDatePicker
                      selected={formData.to_date}
                      onChange={(date) => handleDateChange('to_date', date)}
                      placeholderText="mm/dd/yyyy"
                      required
                    />
                  </div>
                </div>

                {/* Leave Duration Buttons */}
                <div className={styles['form-group']}>
                  <label><Info size={16} /> Leave Duration</label>
                  <div className={styles['duration-toggle-wrapper']}>
                    {[
                      { id: 'full_day', label: 'Full Day' },
                      { id: 'first_half', label: 'First Half' },
                      { id: 'second_half', label: 'Second Half' }
                    ].map(type => (
                      <button
                        key={type.id}
                        type="button"
                        className={`${styles['duration-btn']} ${formData.leave_duration === type.id ? styles['active'] : ''}`}
                        onClick={() => handleDurationChange(type.id)}
                      >
                        {type.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Reason */}
                <div className={styles['form-group']}>
                  <label><ClipboardList size={16} /> Reason for Leave</label>
                  <textarea
                    name="reason"
                    value={formData.reason}
                    onChange={handleChange}
                    placeholder="Explain the purpose of your leave request..."
                    rows={4}
                    className={styles['form-textarea']}
                  />
                </div>
              </>
            )}
          </div>

          <div className={styles['drawer-footer']}>
            <button type="button" className={styles['cancel-btn']} onClick={onClose}>
              Discard
            </button>
            <button type="submit" className={styles['save-btn']} disabled={loading}>
              {loading ? <Loader2 size={18} className={styles['spinner']} /> : <Save size={18} />}
              <span>{request ? 'Update Request' : 'Submit Request'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LeaveRequestDrawer;
